import { type FocusEvent } from 'react';
import { getFormattedDateValue, parseDisplayDateToServerDate } from '../../utils/formatters';

/**
 * Gets the DatePickerInput value, onChange and onBlur props that handle parsing and formatting of dates
 * @param field react-hook-form field props
 */
export function getDatePickerInputProps(field: {
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
}) {
  return {
    value: getFormattedDateValue(field.value),
    /**
     * If the date can be converted back and fort and remain the same,
     * it will be converted to the server-date format and be a valid date,
     * otherwise, the original input value remains and the date validation
     * fails
     */
    onChange: (value: string) => field.onChange(parseDisplayDateToServerDate(value)),

    /**
     * Does a best-effort conversion from any date format to the server-date format.
     * The supported formats depend on the browser and OS locale.
     */
    onBlur: (ev: FocusEvent<HTMLInputElement, HTMLElement>) => {
      field.onChange(parseDisplayDateToServerDate(field.value, true));

      // Only report onBlur if the focus is moving away from the component
      // not when the focus moves to the datepicker dropdown
      if (ev.relatedTarget && !isElementInsideDatePicker(ev.relatedTarget)) {
        field.onBlur();
      }
    },
  };
}

/** Returns true if the element is or is contained in a component
 * with the datepicker dropdown attributes
 *
 */
export function isElementInsideDatePicker(element: HTMLElement) {
  return element.closest('[data-datepicker-dropdown]') !== null;
}

/**
 * Returns the attributes that identify the datepicker dropdown component
 */
export const datePickerDropdownAttributes = {
  'data-datepicker-dropdown': true,
};
